import React from 'react';
import { cloneDeep } from 'lodash';
import { useFormContext } from 'react-hook-form';

import { isLangRtl } from '$/shared/i18n';
import { PageBuilderState, PageMode, PageName } from '$/components/ui/Input/PageBuilder.types';
import { PageBuilderProps } from './PagesEditor.types';
import { useHasChangedDeep } from '$/hooks/useHasChanged';

export const PageEditorContext = React.createContext<PageBuilderState>({} as PageBuilderState);

export const usePageEditorContext = () => {
  const context = React.useContext(PageEditorContext);
  if (!context) {
    throw new Error('usePageEditorContext must be used within a PageEditorContext');
  }
  return context;
};

export const usePageEditorState = (props: PageBuilderProps): PageBuilderState => {
  const formContext = useFormContext();

  const base: PageBuilderState = React.useMemo(() => {
    if ('ewebinar' in props) {
      return {
        ewebinar: cloneDeep(props.ewebinar), // clone it here to avoid mutating the original object that is used as default values when changing colors, it will cause the form reset in wrong values
        ewebinarId: props.ewebinar?.id,
        set: props.set,
        presenters: props.ewebinar?.presenters ?? [],
        themeSettings: props.ewebinar?.themeSettings,
        ctaConfig:
          props.ewebinar && props.set
            ? {
                showCTA: props.pagename !== PageName.thankyouPage,
                publicWebinar: props.ewebinar,
                set: props.set,
              }
            : undefined,
        hasPreRendered: props.hasPreRendered,
        hideBranding: Boolean(props.ewebinar?.team.settings?.hideBranding),
        isFixedNav: false,
        isPublic: props.isPublic,
        isRtl: isLangRtl(props.ewebinar?.viewerRoomSettings.language ?? 'en'),
        language: props.ewebinar?.viewerRoomSettings.language ?? 'en',
        logoMediaRef: props.ewebinar?.logoMediaRef ?? undefined,
        mode: props.mode,
        page: props.pagename ?? PageName.registrationPage,
        setMode: props.setMode,
        teamId: props.ewebinar?.team.id,
      };
    }

    return {
      hasPreRendered: props.hasPreRendered,
      hideBranding: Boolean(props.series.team.settings?.hideBranding),
      isFixedNav: false,
      isPublic: props.isPublic,
      isRtl: isLangRtl(props.series.language ?? 'en'),
      language: props.series.language ?? 'en',
      logoMediaRef: props.series.logoMediaRef ?? undefined,
      mode: props.mode,
      page: props.pagename ?? PageName.registrationPage,
      setMode: props.setMode,
      series: cloneDeep(props.series),
      seriesId: props.series?.id,
      teamId: props.series.team.id,
      themeSettings: props.series.themeSettings,
    };
  }, [props]);

  const themeSettingsInput = formContext?.watch(
    'themeSettings',
    formContext?.formState.defaultValues?.themeSettings
  );
  const { changeVersion } = useHasChangedDeep([themeSettingsInput]);
  const themeSettings = React.useMemo(() => {
    let themeSettings = base.themeSettings;
    if (base.mode !== PageMode.public && changeVersion) {
      themeSettings = {
        actionColor: themeSettingsInput?.actionColor ?? themeSettings.actionColor,
        primaryColor: themeSettingsInput?.primaryColor ?? themeSettings.primaryColor,
        font: themeSettingsInput?.font ?? themeSettings.font,
        fontCssUrl: themeSettingsInput?.fontCssUrl ?? themeSettings.fontCssUrl,
        fontSize: themeSettingsInput?.fontSize ?? themeSettings.fontSize,
        borderRadius: themeSettingsInput?.borderRadius ?? themeSettings.borderRadius,
      };
    }
    return themeSettings;
  }, [base.mode, base.themeSettings, changeVersion, themeSettingsInput]);

  const value: PageBuilderState = React.useMemo(() => {
    return {
      ...base,
      themeSettings,
    };
  }, [base, themeSettings]);

  return value;
};

export const PageEditorProvider: React.FC<PageBuilderProps> = (props) => {
  const value = usePageEditorState(props);
  return <PageEditorContext.Provider value={value}>{props.children}</PageEditorContext.Provider>;
};
